/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _Object$assign from "@babel/runtime-corejs3/core-js-stable/object/assign";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";

/** @module browser/classes/thread */
import { log } from '@codistica/core'; // TODO: ADD @instance WHERE NEEDED!

/**
 * @typedef threadOptionsType
 * @property {number} [activeTimeThreshold=100] - Minimum measured time in milliseconds that must be accumulated before using for calculations.
 * @property {string|null} [threadIndex=null] - Thread index in Loader.
 */

/**
 * @classdesc Thread class for @codistica/browser Loader class.
 */

var Thread = /*#__PURE__*/function () {
  // TODO: ADD activeLoadedThreshold. SPECIFY UNITS IN DESCRIPTION, ALSO FOR activeTimeThreshold

  /**
   * @description Constructor.
   * @param {threadOptionsType} [options] - Thread options.
   */
  function Thread() {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7;

    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Thread);

    var that = this;
    this.threadIndex = typeof options.threadIndex === 'string' ? options.threadIndex : null;
    this.payload = null;
    this.options = {
      activeTimeThreshold: typeof options.activeTimeThreshold === 'number' ? options.activeTimeThreshold : 100
    };
    this.timing = {
      activeTimestamp: null,
      // TODO: RENAME
      activeTime: 0,
      // TODO: RENAME
      activeLoaded: 0 // TODO: RENAME

    };
    this.status = {
      firstChunkReceived: false,
      isUpdated: false,

      /**
       * @description Getter: isActive.
       * @returns {boolean} Value.
       */
      get isActive() {
        // TODO: RENAME
        return that.timing.activeTimestamp !== null;
      }

    };
    this.performance = {
      rtt: null,
      throughput: null
    }; // BIND METHODS

    this.run = _bindInstanceProperty(_context = this.run).call(_context, this);
    this.clear = _bindInstanceProperty(_context2 = this.clear).call(_context2, this);
    this.reset = _bindInstanceProperty(_context3 = this.reset).call(_context3, this);
    this.onEndHandler = _bindInstanceProperty(_context4 = this.onEndHandler).call(_context4, this);
    this.onComputableProgressHandler = _bindInstanceProperty(_context5 = this.onComputableProgressHandler).call(_context5, this);
    this.onDeltaLoadedHandler = _bindInstanceProperty(_context6 = this.onDeltaLoadedHandler).call(_context6, this);
    this.onHeadersHandler = _bindInstanceProperty(_context7 = this.onHeadersHandler).call(_context7, this);
  }
  /**
   * @description Run specified payload.
   * @param {Object<string,*>} payload - Payload.
   */


  _createClass(Thread, [{
    key: "run",
    value: function run(payload) {
      this.payload = payload; // ATTACH HANDLERS

      payload.once('end', this.onEndHandler);
      payload.on('computableprogress', this.onComputableProgressHandler);
      payload.on('deltaloaded', this.onDeltaLoadedHandler);
      payload.once('headers', this.onHeadersHandler);
      payload.send();
    }
  }, {
    key: "clear",
    value: function clear() {
      this.payload = null;
      this.timing.activeTimestamp = null;
      this.status.firstChunkReceived = false;
    }
  }, {
    key: "reset",
    value: function reset() {
      // RESET INSTANCE
      _Object$assign(this, new Thread({
        activeTimeThreshold: this.options.activeTimeThreshold,
        threadIndex: this.threadIndex
      }));
    }
    /**
     * @description Callback for end event.
     * @param {{that: Object<string,*>}} arg - Event passed arguments.
     */

  }, {
    key: "onEndHandler",
    value: function onEndHandler(_ref) {
      var that = _ref.that;
      // DETACH HANDLERS
      that.off('deltaloaded', this.onDeltaLoadedHandler);
      that.off('computableprogress', this.onComputableProgressHandler);
      this.clear();
    }
    /**
     * @description Callback for computableProgress event.
     * @param {{that: Object<string,*>, now: number}} arg - Event passed arguments.
     */

  }, {
    key: "onComputableProgressHandler",
    value: function onComputableProgressHandler(_ref2) {
      var that = _ref2.that,
          now = _ref2.now;
      var activeTime;

      if (this.timing.activeTimestamp === null) {
        this.timing.activeTimestamp = now;
      } // GET THROUGHPUT


      activeTime = now - this.timing.activeTimestamp + this.timing.activeTime;

      if (activeTime >= this.options.activeTimeThreshold) {
        var _context8;

        this.performance.throughput = this.timing.activeLoaded / activeTime;
        this.timing.activeTimestamp = now;
        this.timing.activeTime = 0;
        this.timing.activeLoaded = 0;
        this.status.isUpdated = true;
        log.debug('Thread()', _concatInstanceProperty(_context8 = "THREAD ".concat(this.threadIndex, " - THROUGHPUT: ")).call(_context8, this.performance.throughput * 7.8125, " Kb/s"))();
      } else if (that.progress.loaded === that.progress.total) {
        // SAVE ACCUMULATED ACTIVE TIME FOR NEXT PAYLOAD
        this.timing.activeTime = activeTime;
      }
    }
    /**
     * @description Callback for deltaLoaded event.
     * @param {{deltaLoaded: number}} arg - Event passed arguments.
     */

  }, {
    key: "onDeltaLoadedHandler",
    value: function onDeltaLoadedHandler(_ref3) {
      var deltaLoaded = _ref3.deltaLoaded;

      if (this.status.firstChunkReceived) {
        this.timing.activeLoaded += deltaLoaded;
      } else {
        this.status.firstChunkReceived = true;
      }
    }
    /**
     * @description Callback for headers event.
     * @param {{now: number}} arg - Event passed arguments.
     */

  }, {
    key: "onHeadersHandler",
    value: function onHeadersHandler(_ref4) {
      var _context9;

      var now = _ref4.now;
      // GET LATENCY
      this.performance.rtt = now - this.payload.stats.sendTimestamp;
      log.debug('Thread()', _concatInstanceProperty(_context9 = "THREAD ".concat(this.threadIndex, " - LATENCY: ")).call(_context9, this.performance.rtt, " ms"))();
    }
  }]);

  return Thread;
}();

export { Thread };